import {
  ProductType,
  replaceType,
  Review,
  ComparatorContext,
  KiziLogger,
} from '@kizi/comparator-core';
import React, { useContext } from 'react';
import { getRandomNumber } from '../../utils/global';

type Token = {
  token_type: 'Bearer';
  expires_in: number;
  ext_expires_in: number;
  access_token: string;
  date: number;
};

interface UseTokenResult<TError = any> {
  token: Token | undefined;
  isLoading: boolean;
  error: TError | undefined;
}

export function handleToken(token, setRefresh, date: number) {
  if (token) {
    if (token.date + token.expires_in * 1000 < date) {
      setRefresh(getRandomNumber());
    }
  }
}

export const useToken = <TError = any>(): UseTokenResult<TError> => {
  const [refresh, setRefresh] = React.useState<number>(getRandomNumber());
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<TError | undefined>(undefined);
  const [token, setToken] = React.useState<Token | undefined>(undefined);
  React.useEffect(() => {
    setIsLoading(true);
    setToken(undefined);
    setError(undefined);
  }, [refresh]);
  React.useEffect(() => {
    const interval = setInterval(
      () => handleToken(token, setRefresh, Date.now()),
      10000,
    );
    return () => {
      clearInterval(interval);
    };
  }, [token]);
  return {
    token,
    isLoading,
    error,
  };
};

export const companyIdToProductIdMap = {
  CBC10000: [
    'krn:kizi:product-supplier:d00530a9',
    'krn:kizi:product-supplier:1ed9ff8c',
    '156',
    '157',
  ],
  CBC10001: [
    'krn:kizi:product-supplier:bc1af48d',
    'krn:kizi:product-supplier:49b72942',
    '181',
    '3019',
  ],
  CBC10002: ['krn:kizi:product-supplier:922f36f5', '135'],
  CBC10003: ['krn:kizi:product-supplier:0d61c04b', '74'],
  CBC10004: ['krn:kizi:product-supplier:6e3e923e'],
  CBC10006: [
    'krn:kizi:product-supplier:90074450',
    'krn:kizi:product-supplier:bca1575a',
    '1234569',
    '120',
  ],
  CBC10007: ['krn:kizi:product-supplier:433ecf3e', '94'],
  CBC10008: [
    'krn:kizi:product-supplier:dca9e9e9',
    'krn:kizi:product-supplier:811fe03d',
    '5003',
  ],
  CBC10009: ['krn:kizi:product-supplier:437ee649', '139'],
  CBC10010: [
    'krn:kizi:product-supplier:2450018c2a87',
    '1234568',
    'krn:kizi:product-supplier:31d81ee2',
    '131',
  ],
  CBC10011: ['79', 'krn:kizi:product-supplier:3e943c16'],
  CBC10012: [
    'krn:kizi:product-supplier:ccfe7b49',
    'krn:kizi:product-supplier:c22ebf67',
    '67',
  ],
  CBC10013: [
    'krn:kizi:product-supplier:9ec4c1a7',
    '3029',
    'krn:kizi:product-supplier:1e41efbe',
    '5008',
    'N013',
    '150',
  ],
  CBC10014: ['krn:kizi:product-supplier:07b617f5', '69'],
  CBC10015: ['3028'],
  CBC10016: ['krn:kizi:product-supplier:fdea1ff9', 'H043', '168'],
  CBC10017: ['krn:kizi:product-supplier:c511fefc', '5002', '111'],
  CBC10018: ['173', 'krn:kizi:product-supplier:d5a47015'],
  CBC10019: ['krn:kizi:product-supplier:8f4a095e', '3025', '0109'],
  CBC10020: [
    'krn:kizi:product-supplier:008fa9f7',
    'krn:kizi:product-supplier:442dd4ad',
    '75',
    'A059',
    '82',
  ],
  CBC10021: ['A121'],
  CBC10022: ['180', 'krn:kizi:product-supplier:aaeebf38', 'U705'],
  CBC10023: ['3002'],
  CBC10024: ['3036', 'krn:kizi:product-supplier:01bc2572', 'R515'],
  CBC10028: ['80'],
  CBC10036: ['142'],
  CBC10042: ['179'],
  CBC10046: ['krn:kizi:product-supplier:b1d31640'],
  CBC10115: ['krn:kizi:product-supplier:9827f412'],
  CBC10047: ['krn:kizi:product-supplier:8312ea6c'],
  CBC10048: ['krn:kizi:product-supplier:ee3078dc'],
  CBC10049: ['krn:kizi:product-supplier:647cd2c1'],
  CBC10050: ['krn:kizi:product-supplier:d8ded272'],
  CBC10051: ['krn:kizi:product-supplier:42c424ee', '184'],
  CBC10151: ['krn:kizi:product-supplier:69e12129'],
  CBC10149: ['krn:kizi:product-supplier:f95703c9'],
  CBC10153: ['krn:kizi:product-supplier:14263505'],
  CBC10052: ['341'],
  CBC10054: ['60'],
  CBC10055: ['235'],
  CBC10056: ['227'],
  CBC10057: ['11'],
  CBC10058: ['354'],
  CBC10059: ['krn:kizi:product-supplier:7ac52024', '200'],
  CBC10060: ['17'],
  CBC10061: ['21'],
  CBC10062: ['6'],
  CBC10063: ['344'],
  CBC10064: ['213'],
  CBC10067: ['15'],
  CBC10068: ['331'],
  CBC10069: ['243'],
  CBC10070: ['24'],
  CBC10071: ['348'],
  CBC10072: ['288'],
  CBC10073: ['207'],
  CBC10074: ['13'],
  CBC10075: ['276'],
  CBC10076: ['18'],
  CBC10077: ['229'],
  CBC10079: ['252'],
  CBC10083: ['59'],
  CBC10084: ['323'],
  CBC10085: ['246'],
  CBC10086: ['360'],
  CBC10087: ['66'],
  CBC10088: ['54'],
  CBC10089: ['338'],
  CBC10090: ['55'],
  CBC10091: ['351'],
  CBC10092: ['317'],
  CBC10093: ['332'],
  CBC10094: ['335'],
  CBC10095: ['233'],
  CBC10096: ['48'],
  CBC10097: ['22'],
  CBC10098: ['56'],
  CBC10099: ['269'],
  CBC10100: ['370'],
  CBC10101: ['368'],
  CBC10162: ['382'],
  CBC10164: ['395'],
  CBC10165: ['424'],
  CBC10168: ['379'],
  CBC10166: ['418'],
  CBC10169: ['279'],
  CBC10170: ['373'],
  CBC10191: ['320'],
  CBC10192: ['206'],
  CBC10102: ['krn:kizi:product-supplier:7df8ed90', '106'],
  CBC10103: ['1234560'],
  CBC10104: ['195'],
  CBC10105: ['5005'],
  CBC10106: ['krn:kizi:product-supplier:cd8469b0', '77'],
  CBC10107: ['88'],
  CBC10108: ['5001'],
  CBC10109: ['krn:kizi:product-supplier:d7ed4f69', '99'],
  CBC10110: ['krn:kizi:product-supplier:d2a1ab4cf913', '104'],
  CBC10111: ['krn:kizi:product-supplier:7de47c9b', '112'],
  CBC10112: ['1234568'],
  CBC10113: ['krn:kizi:product-supplier:929171e738a0', '5004'],
  CBC10114: ['5014', '5013'],
  CBC10116: ['krn:kizi:product-supplier:586932ac1a16', '5006'],
  CBC10117: ['krn:kizi:product-supplier:47c92e34', '143'],
  CBC10118: ['5007'],
  CBC10119: ['krn:kizi:product-supplier:64ad0fa5', '159'],
  CBC10120: ['5009'],
  CBC10121: ['5010'],
  CBC10122: ['krn:kizi:product-supplier:a6ed843c331d', '165'],
  CBC10123: ['1234567'],
  CBC10124: ['krn:kizi:product-supplier:95939ee0', '171'],
  CBC10125: ['krn:kizi:product-supplier:920c80ab', '175'],
  CBC10126: ['krn:kizi:product-supplier:583203375a51', '33', '12345671'],
  CBC10127: ['krn:kizi:product-supplier:0b5f96f6'],
  CBC10128: ['krn:kizi:product-supplier:d2fb67ab', '5016'],
  CBC10129: ['5011'],
  CBC10130: ['krn:kizi:product-supplier:f493a1f7437a', '190'],
  CBC10131: ['krn:kizi:product-supplier:de3bb7e32d3b', '5015'],
  CBC10132: ['krn:kizi:product-supplier:27769607', '192'],
  CBC10133: ['krn:kizi:product-supplier:5e572afb', '194'],
  CBC10134: ['5012'],
  CBC10135: ['krn:kizi:product-supplier:6993b0ea25c7', '5017'],
  CBC10137: ['3003'],
  CBC10138: ['krn:kizi:product-supplier:b359d6f25616', '5018'],
  CBC10139: ['krn:kizi:product-supplier:41f20d22', '133'],
  CBC10140: ['krn:kizi:product-supplier:0baf0362'],
  CBC10142: ['krn:kizi:product-supplier:6a067e0c'],
  CBC10144: ['krn:kizi:product-supplier:160a1c7c'],
  CBC10145: ['3007'],
  CBC10146: ['krn:kizi:product-supplier:2bb56d93'],
  CBC10196: ['41'],
  CBC10201: ['44'],
  CBC10154: ['63', 'krn:kizi:product-supplier:ed616322'],
  CBC10155: ['66', '64', 'krn:kizi:product-supplier:a8d62220'],
  CBC10197: ['42'],
  CBC10158: ['65', 'krn:kizi:product-supplier:ec6dcaa6'],
  CBC10159: ['56', 'krn:kizi:product-supplier:80dfb88a'],
  CBC10182: ['60010'],
  CBC10178: ['60007'],
  CBC10185: ['60004'],
  CBC10194: ['60003'],
  CBC10160: ['72', 'krn:kizi:product-supplier:f45db281'],
  CBC10195: ['2002'],
  CBC10152: ['21', 'krn:kizi:product-supplier:f318d224'],
  CBC10157: ['33', 'krn:kizi:product-supplier:603f4baf'],
  CBC10156: ['67', 'krn:kizi:product-supplier:481136c4'],
  CBC10167: ['R734', '3037'],
  CBC10171: ['421'],
  CBC10172: ['315'],
  CBC10173: ['3032'],
  CBC10174: ['3035'],
  CBC10175: ['3034'],
  CBC10176: ['3033'],
  CBC10177: ['krn:kizi:product-supplier:7d283ded'],
  CBC10179: ['202'],
  CBC10180: ['105'],
  CBC10181: ['107'],
  CBC10183: ['133'],
  CBC10186: ['388'],
  CBC10187: ['50003'],
  CBC10188: ['147'],
  CBC10189: ['50004'],
  CBC10190: ['91'],
  CBC10193: ['164'],
  CBC10198: ['krn:kizi:product-supplier:29f2026921f3', '5016'],
  CBC10199: ['60001'],
  CBC10202: ['50002'],
  CBC10203: ['197'],
  CBC10200: ['40019'],
  CBC10204: ['415'],
  CBC10205: ['krn:kizi:product-supplier:2ab1f4f7b312'],
};

export const useReviews = <TError = any>(type: ProductType) => {
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<TError | undefined>(undefined);
  const [data, setData] = React.useState<Review[] | undefined>(undefined);
  const updatedType = replaceType(type);
  const { name } = useContext(ComparatorContext);
  React.useEffect(() => {
    setIsLoading(true);
    setError(undefined);
    setData(undefined);

    if (
      name.includes('os-') ||
      name === 'cb-health' ||
      name === 'moneysure-event-based' ||
      name === 'avz'
    ) {
      return;
    }

    fetch(`${process.env.KIZI_PUBLIC_TSN_API_BASE_URL}/v1/scores`)
      .then((response) => response.json())
      .then(setData, setError)
      .finally(() => {
        setIsLoading(false);
      })
      .catch((error) => {
        KiziLogger.logError({
          eventId: 0,
          member: 'useReviews hook',
          message: 'Error in fetchData',
          eventName: 'fetchDataError ',
          data: {
            error,
          },
        });
      });
  }, []);

  const reviews = React.useMemo<Review[] | undefined>(
    () => handleReviewsData(data, updatedType.toUpperCase() as ProductType),
    [type, data],
  );
  return {
    isLoading: isLoading,
    error: error,
    reviews,
  };
};

export function handleReviewsData(
  data: Review[] | undefined,
  type: ProductType,
) {
  if (!data || !Array.isArray(data) || !type) return undefined;
  return data
    .filter(({ productType }) => productType === type)
    .map((review) => {
      return {
        ...review,
        providerId: companyIdToProductIdMap[review.companyId],
      };
    });
}

export enum Recommendations {
  RECOMMENDED = 'RECOMMENDED',
  NOT_RECOMMENDED = 'NOT_RECOMMENDED',
  UNSPECIFIED = 'UNSPECIFIED',
}
export type Testimonial = {
  averageScore: number;
  description: string;
  id: string;
  likes: number;
  publicationDate: string;
  recommendation: Recommendations;
  title: string;
  userName: string;
  ratings: { name: string; score: number }[];
  pros: string[];
  cons: string[];
};

export type Testimonials = {
  items: Testimonial[];
  page: {
    currentPage: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
};
export const useTestimonials = <TError = any>(
  id: string,
  page = 0,
  size = 4,
) => {
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<TError | undefined>(undefined);
  const [data, setData] = React.useState<Testimonials | undefined>(undefined);
  const { api } = useContext(ComparatorContext);
  const token = api.credentials.token;

  const url = React.useMemo(() => {
    const u = new URL(
      `${process.env.KIZI_PUBLIC_TSN_API_BASE_URL}/v1/scores/${id}`,
    );
    u.searchParams.append('page', String(page));
    u.searchParams.append('size', String(size));
    return u.toString();
  }, [id, page, size]);

  React.useEffect(() => {
    if (!id) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(undefined);
    setData(undefined);

    if (!token) return;
    fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((response) => response.json())
      .then(setData, setError)
      .finally(() => {
        setIsLoading(false);
      });
  }, [token, url, id]);

  const testimonials = React.useMemo(() => {
    if (!data || !id) return undefined;
    return data;
  }, [id, data]);

  return {
    isLoading: isLoading,
    error: error,
    testimonials,
  };
};
